// Clause + paren-frame state machine for EditorContext projection.
// Walks the token stream from statement start to cursor, tracking
// the current SQL clause and paren depth/range.
//
// Also exports: tokenizeUntil (clause-machine tokenizer),
// findStatementBounds (semicolon-based statement isolation).

import type { ParenFrame, SqlClause } from '../model/editorContext';
import type { SqlRange } from '../model';
import { buildLineIndex, rangeFromOffsets } from './lineIndex';
import {
  isBareIdentChar,
  isBareIdentStart,
  isOperatorChar,
  skipBacktickId,
  skipBlockComment,
  skipBracketedId,
  skipDoubleQuoted,
  skipSingleQuoted,
  skipToEol,
  tryDollarQuoted,
} from './scanPrimitives';

// ---------------------------------------------------------------------------
// SQL keyword set used for kw vs identifier discrimination
// ---------------------------------------------------------------------------

export const SQL_KEYWORDS = new Set([
  'SELECT', 'FROM', 'WHERE', 'GROUP', 'HAVING', 'ORDER', 'LIMIT', 'OFFSET',
  'JOIN', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'CROSS', 'NATURAL', 'OUTER',
  'ON', 'USING', 'VALUES', 'SET', 'INTO', 'INSERT', 'UPDATE', 'DELETE',
  'WITH', 'AS', 'BY', 'TOP', 'FETCH', 'NEXT', 'ROW', 'ROWS', 'ONLY',
  'TIES', 'PERCENT', 'MERGE', 'RETURNING', 'CASE', 'WHEN', 'THEN', 'ELSE',
  'END', 'AND', 'OR', 'NOT', 'IN', 'EXISTS', 'BETWEEN', 'LIKE', 'IS',
  'NULL', 'DISTINCT', 'ALL', 'ANY', 'SOME', 'UNION', 'INTERSECT', 'EXCEPT',
  'BEGIN', 'COMMIT', 'ROLLBACK', 'TRANSACTION', 'CREATE', 'ALTER', 'DROP',
  'TABLE', 'VIEW', 'INDEX', 'PROCEDURE', 'FUNCTION', 'TRIGGER',
  // Oracle hierarchical query clauses (`CONNECT BY` / `START WITH`).
  'CONNECT', 'START',
  // `FOR` opens a row-lock / result-format clause (`FOR UPDATE`, `FOR SHARE`,
  // `FOR XML`, `FOR JSON`). It is in the set so it survives as `prevKw` and the
  // DML arms below can tell `SELECT … FOR UPDATE` from a real UPDATE statement.
  'FOR',
  // Privilege statements. Their privilege list contains DML keywords
  // (`GRANT SELECT, INSERT ON …`) that must NOT set a clause — see the
  // statement-kind gate in `computeClauseContext`.
  // `TO` closes a privilege statement's object list (`GRANT … ON t TO role`).
  'GRANT', 'REVOKE', 'TO',
  // T-SQL's `OUTPUT` is its RETURNING clause. (It is also a parameter direction
  // in a procedure signature; the resolver's own OUTPUT-list walk is what
  // distinguishes them, so naming the clause here is safe either way.)
  'OUTPUT',
]);

// ---------------------------------------------------------------------------
// Internal token type (clause state machine only — not public)
// ---------------------------------------------------------------------------

export type ClauseToken = {
  readonly kind: 'keyword' | 'identifier' | 'literal' | 'operator'
    | 'lparen' | 'rparen' | 'comma' | 'dot' | 'semicolon';
  readonly text: string;
  readonly start: number;
  readonly end: number;
};

// ---------------------------------------------------------------------------
// Tokenizer (engine-aware, allocation-light)
// ---------------------------------------------------------------------------

export function tokenizeUntil(
  source: string,
  from: number,
  to: number,
  engine: string,
): ClauseToken[] {
  const out: ClauseToken[] = [];
  let i = from;
  while (i < to) {
    const c = source.charCodeAt(i);
    if (c === 0x20 || c === 0x09 || c === 0x0a || c === 0x0d) { i += 1; continue; }
    if (c === 0x2d && source.charCodeAt(i + 1) === 0x2d) { i = skipToEol(source, i + 2, to); continue; }
    if (engine === 'mysql' && c === 0x23) { i = skipToEol(source, i + 1, to); continue; }
    if (c === 0x2f && source.charCodeAt(i + 1) === 0x2a) { i = skipBlockComment(source, i + 2, to); continue; }
    if ((c === 0x4e || c === 0x6e) && source.charCodeAt(i + 1) === 0x27) {
      const end = skipSingleQuoted(source, i + 2, to);
      out.push({ kind: 'literal', text: source.slice(i, end), start: i, end });
      i = end; continue;
    }
    if (engine === 'postgres' && c === 0x24) {
      const end = tryDollarQuoted(source, i, to);
      if (end !== -1) { out.push({ kind: 'literal', text: source.slice(i, end), start: i, end }); i = end; continue; }
    }
    if (c === 0x27) {
      const end = skipSingleQuoted(source, i + 1, to);
      out.push({ kind: 'literal', text: source.slice(i, end), start: i, end });
      i = end; continue;
    }
    if ((engine === 'mssql' || engine === 'sqlite') && c === 0x5b) {
      const end = skipBracketedId(source, i + 1, to);
      out.push({ kind: 'identifier', text: source.slice(i, end), start: i, end });
      i = end; continue;
    }
    if (engine === 'mysql' && c === 0x60) {
      const end = skipBacktickId(source, i + 1, to);
      out.push({ kind: 'identifier', text: source.slice(i, end), start: i, end });
      i = end; continue;
    }
    if (c === 0x22) {
      const end = skipDoubleQuoted(source, i + 1, to);
      out.push({ kind: 'identifier', text: source.slice(i, end), start: i, end });
      i = end; continue;
    }
    if (c === 0x28) { out.push({ kind: 'lparen', text: '(', start: i, end: i + 1 }); i += 1; continue; }
    if (c === 0x29) { out.push({ kind: 'rparen', text: ')', start: i, end: i + 1 }); i += 1; continue; }
    if (c === 0x2c) { out.push({ kind: 'comma', text: ',', start: i, end: i + 1 }); i += 1; continue; }
    if (c === 0x2e) { out.push({ kind: 'dot', text: '.', start: i, end: i + 1 }); i += 1; continue; }
    if (c === 0x3b) { out.push({ kind: 'semicolon', text: ';', start: i, end: i + 1 }); i += 1; continue; }
    if (c >= 0x30 && c <= 0x39) {
      let end = i + 1;
      while (end < to) {
        const d = source.charCodeAt(end);
        if ((d >= 0x30 && d <= 0x39) || d === 0x2e || d === 0x65 || d === 0x45) end += 1;
        else break;
      }
      out.push({ kind: 'literal', text: source.slice(i, end), start: i, end });
      i = end; continue;
    }
    if (isBareIdentStart(c)) {
      let end = i + 1;
      while (end < to && isBareIdentChar(source.charCodeAt(end))) end += 1;
      const text = source.slice(i, end);
      out.push({ kind: SQL_KEYWORDS.has(text.toUpperCase()) ? 'keyword' : 'identifier', text, start: i, end });
      i = end; continue;
    }
    if (isOperatorChar(c)) {
      let end = i + 1;
      while (end < to && isOperatorChar(source.charCodeAt(end))) end += 1;
      out.push({ kind: 'operator', text: source.slice(i, end), start: i, end });
      i = end; continue;
    }
    i += 1;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Statement boundary (semicolon split)
// ---------------------------------------------------------------------------

export function findStatementBounds(
  source: string,
  offset: number,
  engine: string,
): { start: number; end: number } {
  const n = source.length;
  const separators: number[] = [];
  let i = 0;
  while (i < n) {
    const c = source.charCodeAt(i);
    if (c === 0x2d && source.charCodeAt(i + 1) === 0x2d) { i = skipToEol(source, i + 2); continue; }
    if (engine === 'mysql' && c === 0x23) { i = skipToEol(source, i + 1); continue; }
    if (c === 0x2f && source.charCodeAt(i + 1) === 0x2a) { i = skipBlockComment(source, i + 2); continue; }
    if (c === 0x27) { i = skipSingleQuoted(source, i + 1); continue; }
    if (engine === 'postgres' && c === 0x24) {
      const end = tryDollarQuoted(source, i);
      if (end !== -1) { i = end; continue; }
    }
    if ((engine === 'mssql' || engine === 'sqlite') && c === 0x5b) { i = skipBracketedId(source, i + 1); continue; }
    if (engine === 'mysql' && c === 0x60) { i = skipBacktickId(source, i + 1); continue; }
    if (c === 0x22) { i = skipDoubleQuoted(source, i + 1); continue; }
    if (c === 0x3b) { separators.push(i); i += 1; continue; }
    i += 1;
  }
  let start = 0;
  for (const sep of separators) {
    if (sep < offset) start = sep + 1;
    else break;
  }
  let end = n;
  for (const sep of separators) {
    if (sep >= offset) { end = sep; break; }
  }
  return { start, end };
}

// ---------------------------------------------------------------------------
// Clause + paren-frame state machine
// ---------------------------------------------------------------------------

type ClauseFrame = {
  clause: SqlClause | null;
  parenStart: number | null;
  /** True when the `(` opened a FUNCTION CALL rather than a subquery or a
   *  clause group. Several SQL functions spell their arguments with clause
   *  keywords — `EXTRACT(YEAR FROM ts)`, `TRIM(BOTH 'x' FROM s)`,
   *  `SUBSTRING(s FROM 2)`, `POSITION('x' IN s)` — and letting those set a
   *  clause turned the argument into a relation position, so the resolver
   *  answered with tables and FK join snippets instead of columns. */
  isCall: boolean;
};

/** Identifier-lexed words whose `(` opens a group that legitimately carries
 *  clause keywords, so it must NOT be treated as a function call. `OVER
 *  (PARTITION BY … ORDER BY …)` is the case that matters. */
const CLAUSE_BEARING_GROUPS: ReadonlySet<string> = new Set(['OVER']);

/** Keywords that, when they appear immediately before `(`, mark the paren
 *  as opening a fresh subquery scope. Mirrors
 *  `intellisense/context/keywords.ts:SUBQUERY_OPENING_KEYWORDS` so the
 *  model and IntelliSense classifications agree on whether `(...)` is a
 *  subquery, a value list, or something else. (GRA-7 Slice A6.) */
const SUBQUERY_OPENING_KEYWORDS: ReadonlySet<string> = new Set([
  'FROM', 'JOIN', 'IN', 'EXISTS', 'ANY', 'ALL',
  'LATERAL', 'APPLY', 'AS', 'UNION', 'INTERSECT', 'EXCEPT',
]);

export function computeClauseContext(
  source: string,
  stmtStart: number,
  offset: number,
  engine: string,
): { clause: SqlClause; parenFrame: ParenFrame } | null {
  const tokens = tokenizeUntil(source, stmtStart, offset, engine);
  if (tokens.length === 0) return null;

  const frames: ClauseFrame[] = [{ clause: null, parenStart: null, isCall: false }];
  const parenStack: number[] = [];
  let prevKw = '';
  /** The previous token's text when it was a bare identifier — a call's name.
   *  Empty when the previous token was anything else. */
  let prevIdentText = '';
  let lockClauseOpen = false;
  // The statement's leading keyword, where it changes what later keywords mean.
  // MERGE: `USING` introduces its SOURCE relation rather than a join's USING
  // column list. GRANT/REVOKE: the privilege list is made of DML keywords that
  // name privileges, not clauses, and only `ON` opens a real (relation)
  // position.
  let statementKind: 'merge' | 'grant' | 'delete' | null = null;
  // A join's `USING (cols)` and Postgres's `DELETE … USING <relation>` are the
  // same word in the same statement. The only thing that separates them is
  // whether a JOIN has already opened in this statement: `DELETE FROM a USING
  // b` cannot follow one (the USING tail comes directly after the target),
  // while `… JOIN c USING (id)` always does.
  let sawJoin = false;
  // `OUTPUT` is T-SQL's RETURNING clause — but the same word is a parameter
  // direction (`@p INT OUTPUT`, `EXEC p @x OUTPUT`). Only a DML statement can
  // have a returning clause, so the arm is gated on having seen one.
  let sawDmlVerb = false;

  for (const tok of tokens) {
    const top = frames[frames.length - 1];
    if (tok.kind === 'lparen') {
      parenStack.push(tok.start);
      // Subquery openers — push a fresh `'subquery'` frame so the inside
      // of the paren is classified independently from the parent clause.
      // Matches IntelliSense `detectPositionKind` semantics; any
      // SELECT/FROM/etc. inside will overwrite `'subquery'` once seen.
      const opensSubquery = !!prevKw && SUBQUERY_OPENING_KEYWORDS.has(prevKw);
      const pushedClause: SqlClause | null = opensSubquery ? 'subquery' : top.clause;
      // A `(` whose previous token is a plain identifier is a call's argument
      // list (`EXTRACT(`, `my_fn(`). A subquery opener wins over that reading,
      // and so does a window definition: `OVER` lexes as an identifier here but
      // `OVER (PARTITION BY … ORDER BY …)` really does carry clause keywords.
      frames.push({
        clause: pushedClause,
        parenStart: tok.start,
        isCall: !opensSubquery && prevIdentText !== '' && !CLAUSE_BEARING_GROUPS.has(prevIdentText),
      });
      prevKw = ''; prevIdentText = ''; continue;
    }
    if (tok.kind === 'rparen') {
      if (frames.length > 1) frames.pop();
      if (parenStack.length > 0) parenStack.pop();
      prevKw = ''; prevIdentText = ''; continue;
    }
    if (tok.kind === 'keyword') {
      const kw = tok.text.toUpperCase();
      if (statementKind === null && frames.length === 1) {
        if (kw === 'MERGE') statementKind = 'merge';
        else if (kw === 'GRANT' || kw === 'REVOKE') statementKind = 'grant';
        else if (kw === 'DELETE') statementKind = 'delete';
      }
      if (kw === 'JOIN') sawJoin = true;
      if (kw === 'INSERT' || kw === 'UPDATE' || kw === 'DELETE' || kw === 'MERGE') {
        sawDmlVerb = true;
      }
      // Inside a call's argument list, a clause keyword is part of the
      // function's own syntax, not a clause of the enclosing statement.
      if (!top.isCall) {
        applyClauseKeyword(
          kw, top, prevKw, frames.length, lockClauseOpen, statementKind, sawDmlVerb, sawJoin,
        );
      }
      // `FOR` opens a row-lock clause whose strength words may be plain
      // identifiers to this tokenizer (`FOR NO KEY UPDATE`), so `prevKw` alone
      // cannot carry the context to the `UPDATE` arm. The flag stays up until
      // the strength keyword is consumed or a real clause keyword arrives.
      lockClauseOpen = kw === 'FOR' || (lockClauseOpen && !CLAUSE_SETTING_KEYWORDS.has(kw));
      prevKw = kw; prevIdentText = ''; continue;
    }
    if (tok.kind === 'comma' || tok.kind === 'dot') { prevIdentText = ''; continue; }
    prevIdentText = tok.kind === 'identifier' ? tok.text.toUpperCase() : '';
    if (tok.kind !== 'semicolon') prevKw = '';
  }

  const frame = frames[frames.length - 1];
  const clause = frame.clause ?? 'other';
  const depth = parenStack.length;
  let enclosingRange: SqlRange | undefined;

  if (depth > 0) {
    const lineIdx = buildLineIndex(source);
    const openAt = parenStack[parenStack.length - 1];
    const closeAt = findMatchingCloseParen(source, openAt + 1, engine);
    enclosingRange = rangeFromOffsets(lineIdx, openAt, closeAt > openAt ? closeAt : offset);
  }

  return { clause, parenFrame: { depth, enclosingRange } };
}

/** Keywords that establish a clause — reaching one closes any pending `FOR`
 *  row-lock clause. */
const CLAUSE_SETTING_KEYWORDS: ReadonlySet<string> = new Set([
  'SELECT', 'FROM', 'WHERE', 'GROUP', 'HAVING', 'ORDER', 'JOIN', 'ON', 'USING',
  'VALUES', 'SET', 'INSERT', 'DELETE', 'RETURNING', 'LIMIT', 'OFFSET', 'FETCH',
]);

function applyClauseKeyword(
  kw: string,
  frame: ClauseFrame,
  prevKw: string,
  depth: number,
  lockClauseOpen: boolean,
  statementKind: 'merge' | 'grant' | 'delete' | null,
  sawDmlVerb: boolean,
  sawJoin: boolean,
): void {
  // A privilege list is spelled with DML keywords — `GRANT SELECT, INSERT,
  // UPDATE ON t TO role`. Letting them set clauses made `GRANT SELECT ON ▮`
  // a completed SELECT projection, answering FROM / AS / INTO where the
  // relation being granted belongs.
  if (statementKind === 'grant') {
    // `ON <object>` is the only relation position; `TO role` / `FROM role`
    // names a grantee, which is not in the schema snapshot at all.
    if (kw === 'ON') frame.clause = 'from';
    else if (kw === 'TO' || kw === 'FROM') frame.clause = 'other';
    return;
  }
  switch (kw) {
    // `WITH ...` is a CTE introducer only as the statement's LEADING keyword —
    // i.e. while the frame has no clause yet. Once a clause is established the
    // same word is a modifier belonging to that clause, not a CTE:
    // `FETCH FIRST 10 ROWS WITH TIES`, `GROUP BY x WITH ROLLUP`,
    // `FROM t WITH (NOLOCK)`, `FROM f(x) WITH ORDINALITY`. Treating those as
    // 'cte-def' handed the resolver the wrong position for every caret between
    // the modifier and the next clause keyword. The old rule was `depth === 1`
    // (outermost frame only); that still holds — a nested frame always carries
    // its parent's clause or 'subquery', never null — so this only narrows.
    case 'WITH':
      if (depth === 1 && frame.clause === null) frame.clause = 'cte-def';
      break;
    case 'SELECT': frame.clause = 'select'; break;
    case 'FROM': frame.clause = 'from'; break;
    case 'WHERE': frame.clause = 'where'; break;
    // `WITHIN GROUP (…)` is an ordered-set aggregate's own syntax, not the
    // statement's GROUP BY — treating it as one flooded that paren with the
    // group-by emission and hid the only legal token, `ORDER BY`.
    case 'GROUP':
      if (prevKw !== 'WITHIN') frame.clause = 'group-by';
      break;
    case 'HAVING': frame.clause = 'having'; break;
    case 'ORDER': frame.clause = 'order-by'; break;
    // LIMIT / OFFSET / TOP / FETCH — pagination clause; collapsed to one
    // arm because resolvers treat them uniformly (suggest integers / no
    // schema completion).
    case 'LIMIT':
    case 'OFFSET':
    case 'TOP':
    case 'FETCH':
      frame.clause = 'limit-offset';
      break;
    case 'JOIN': frame.clause = 'from'; break;
    // Oracle hierarchical query clauses — `CONNECT BY <predicate>` and
    // `START WITH <predicate>` both take a predicate over the relations
    // already in scope, so they complete like a WHERE. Guarded on an
    // established clause: with none yet the same words are `START WITH 1` in
    // CREATE SEQUENCE or `START TRANSACTION`, which are not predicates.
    // (Before `WITH` stopped introducing a CTE mid-statement, `START WITH`
    // accidentally landed on 'cte-def' and reached the expression arm from
    // there; this makes the routing deliberate.)
    case 'CONNECT':
    case 'START':
      if (frame.clause !== null) frame.clause = 'where';
      break;
    case 'ON':
      if (prevKw === 'JOIN' || frame.clause === 'from') frame.clause = 'join-on';
      break;
    // A join's `USING (cols)` is a predicate position; a MERGE's `USING` and
    // Postgres's `DELETE … USING` are where a SOURCE RELATION goes — the
    // DELETE form only before any JOIN has opened, since after one the same
    // word is that join's column list. Without the DELETE arm the whole
    // `USING` tail read as a predicate, so the relation list it actually
    // wants lost to the bound target's columns (pgsql script.10 §2).
    case 'USING':
      frame.clause =
        statementKind === 'merge' || (statementKind === 'delete' && !sawJoin)
          ? 'from'
          : 'join-on';
      break;
    case 'VALUES': frame.clause = 'insert-values'; break;
    case 'SET': frame.clause = 'update-set'; break;
    case 'INSERT': frame.clause = 'insert-columns'; break;
    // A DML keyword inside a `FOR` row-lock clause is a strength, not a
    // statement: `SELECT … FOR UPDATE` / `FOR NO KEY UPDATE` / `FOR SHARE`.
    // Re-clausing there turned the rest of the SELECT into an UPDATE … SET
    // position, so every caret after it completed as if it were a SET list.
    case 'UPDATE':
      if (!lockClauseOpen) frame.clause = 'update-set';
      break;
    // `DELETE ▮` is not yet a predicate position — the target has not been
    // named. Classifying it as one made the very first caret of a DELETE lead
    // with `IS NULL` / `IS NOT NULL` and bury `FROM` ~70 items down. The real
    // predicate position arrives with the statement's own `WHERE`.
    case 'DELETE': frame.clause = 'other'; break;
    // RETURNING — Postgres / Oracle DML returning-clause (and MSSQL `OUTPUT`
    // is treated separately by the resolvers, not via this arm).
    case 'RETURNING':
      frame.clause = 'returning';
      break;
    case 'OUTPUT':
      if (sawDmlVerb) frame.clause = 'returning';
      break;
    // CASE is intentionally NOT handled here. It's an expression construct,
    // not a clause — IntelliSense `detectPositionKind` leaves the outer
    // clause untouched on CASE (`SELECT CASE WHEN | …` stays 'select-list',
    // `WHERE x = CASE WHEN | …` stays 'where').
    default: break;
  }
}

function findMatchingCloseParen(source: string, from: number, engine: string): number {
  let depth = 1;
  let i = from;
  const n = source.length;
  while (i < n && depth > 0) {
    const c = source.charCodeAt(i);
    if (c === 0x2d && source.charCodeAt(i + 1) === 0x2d) { i = skipToEol(source, i + 2); continue; }
    if (c === 0x2f && source.charCodeAt(i + 1) === 0x2a) { i = skipBlockComment(source, i + 2); continue; }
    if (c === 0x27) { i = skipSingleQuoted(source, i + 1); continue; }
    if (engine === 'postgres' && c === 0x24) {
      const end = tryDollarQuoted(source, i);
      if (end !== -1) { i = end; continue; }
    }
    if ((engine === 'mssql' || engine === 'sqlite') && c === 0x5b) { i = skipBracketedId(source, i + 1); continue; }
    if (engine === 'mysql' && c === 0x60) { i = skipBacktickId(source, i + 1); continue; }
    if (c === 0x22) { i = skipDoubleQuoted(source, i + 1); continue; }
    if (c === 0x28) { depth += 1; i += 1; continue; }
    if (c === 0x29) { depth -= 1; if (depth === 0) return i + 1; i += 1; continue; }
    i += 1;
  }
  return from;
}
